<script setup>
// "Membership or pass needed" (AppGateMembersOnly, D31, ME6): the inline step
// before joining a competition whose entry rule asks for a membership. Lists
// the types the person qualifies for with the join verb, and says when an
// application is already with the club. The rule itself comes from
// useMembershipGates().competitionMembershipRule (A3's seam); the host passes
// the result and the page to come back to.
import {computed} from 'vue';
import MembershipTypeRow from '~/components/memberships/MembershipTypeRow.vue';

const props = defineProps({
  /** The competitionMembershipRule result (see useMembershipGates). */
  rule: {type: Object, default: null},
  /** Where "Get" comes back to once the membership is active. */
  returnTo: {type: String, default: null},
  /** The competition's name for "Senior and Junior members join this ladder for free". */
  entityName: {type: String, default: null},
});

const needed = computed(() => !!props.rule && props.rule.eligibility && props.rule.eligibility !== 'Anyone' && !props.rule.holdsCurrent);
const pending = computed(() => !!props.rule?.pendingApplicationId);
const pendingLine = computed(() => props.rule?.pendingApplicationLine
    || 'Your membership application is with the club. Join now and your place is held until they approve it.');
const types = computed(() => props.rule?.qualifyingTypes ?? []);
const typeLink = (t) => ({
  name: 'ClubMembershipJoin',
  params: {clubId: t.clubId, typeId: t.id},
  query: props.returnTo ? {returnTo: props.returnTo} : {},
});
</script>

<template>
  <div v-if="needed" class="tl-card mgc" data-testid="membership-gate">
    <div class="mgc-head">
      <div class="mgc-ic"><v-icon size="20" icon="mdi-card-account-details-outline"/></div>
      <div class="flex-fill">
        <div class="mgc-title">Membership or pass needed</div>
        <div class="mgc-sub">{{ rule.neededLine || 'Get one below, then come back here to join.' }}</div>
        <div v-if="rule.priceLine" class="mgc-price">{{ rule.priceLine }}</div>
      </div>
    </div>
    <div v-if="types.length" class="mgc-list">
      <membership-type-row v-for="t in types" :key="t.id" :type="t" :to="typeLink(t)"/>
    </div>
    <div v-if="pending" class="mgc-pending">
      <v-icon size="16" icon="mdi-clock-outline"/>
      {{ pendingLine }}
    </div>
  </div>
</template>

<style scoped lang="scss">
.mgc { padding: 0; overflow: hidden; }
.mgc-head {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
}
.mgc-ic {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: rgb(var(--v-theme-brandTint));
  color: rgb(var(--v-theme-primary));
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.mgc-title { font-size: 14px; font-weight: 700; letter-spacing: -0.2px; }
.mgc-sub { font-size: 12.5px; color: rgb(var(--v-theme-textSubtle)); margin-top: 2px; line-height: 1.45; }
.mgc-price { font-size: 12.5px; font-weight: 600; margin-top: 4px; }
.mgc-list { border-top: 1px solid rgb(var(--v-theme-borderLight)); }
.mgc-pending {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  font-size: 12.5px;
  color: rgb(var(--v-theme-textSubtle));
  background: rgb(var(--v-theme-light));
  border-top: 1px solid rgb(var(--v-theme-borderLight));
}
</style>
